import { motion, useReducedMotion, useScroll, useTransform } from "motion/react";
import { useRef } from "react";
import { ArrowDown, WashingMachine, ShowerHead, PawPrint, Wifi, Trees } from "lucide-react";
import { amenityQuickBar, type AmenityQuickIcon } from "@/data/resort";
import { HeroPhoto } from "../OptimizedImage";
import { ResortLink } from "../resort-button";
import { Reveal } from "../reveal";

const iconMap: Record<AmenityQuickIcon, typeof Wifi> = {
  laundry: WashingMachine,
  showers: ShowerHead,
  pets: PawPrint,
  wifi: Wifi,
  outdoor: Trees,
};

export function AmenitiesHero() {
  const ref = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);
  const ease = [0.22, 1, 0.36, 1] as const;

  return (
    <section
      ref={ref}
      id="top"
      aria-labelledby="amenities-hero-heading"
      className="relative min-h-[78svh] overflow-hidden bg-forest-deep md:min-h-[70svh]"
    >
      <motion.div
        className="absolute inset-0"
        style={{ y: reduced ? 0 : bgY }}
        initial={reduced ? false : { scale: 1.04 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2.2, ease }}
      >
        <HeroPhoto alt="Guests relaxing outdoors beside their RV at Auburn RV Resort" />
        <div className="overlay-cinematic absolute inset-0" />
      </motion.div>

      <motion.div
        style={{ y: reduced ? 0 : contentY }}
        className="shell relative flex min-h-[78svh] flex-col justify-end pb-34 pt-18 md:min-h-[70svh] md:pb-32 md:pt-20"
      >
        <motion.p
          className="eyebrow text-primary-foreground/80"
          initial={reduced ? false : { opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2, ease }}
        >
          Comfort Between Adventures
        </motion.p>

        <h1
          id="amenities-hero-heading"
          className="display-xl mt-6 max-w-[16ch] text-primary-foreground"
        >
          {["Everything You Need", "to Rest, Recharge", "& Explore"].map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                className="display-xl block"
                initial={reduced ? false : { y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, delay: 0.35 + i * 0.1, ease }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          className="mt-8 max-w-2xl text-base leading-relaxed text-primary-foreground/80 md:text-lg"
          initial={reduced ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease }}
        >
          From fresh showers and on-site laundry to WiFi, outdoor spaces and pet-friendly amenities,
          Auburn RV Resort gives you the essentials for an easy and comfortable stay in the Sierra
          Foothills.
        </motion.p>

        <motion.div
          className="mt-10 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap"
          initial={reduced ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05, ease }}
        >
          <ResortLink variant="gold" href="/reservations/" className="w-full sm:w-auto">
            Check Availability
          </ResortLink>
          <ResortLink variant="onDark" href="#during-your-stay" className="w-full sm:w-auto">
            Explore the Amenities
          </ResortLink>
        </motion.div>

        <motion.p
          className="eyebrow mt-6 text-primary-foreground/60"
          initial={reduced ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2, ease }}
        >
          Pet Friendly · WiFi · Laundry · Showers · Outdoor Spaces
        </motion.p>
      </motion.div>

      <div className="pointer-events-none absolute bottom-48 right-6 hidden items-center gap-3 text-primary-foreground/70 md:flex md:bottom-44">
        <span className="eyebrow">Scroll</span>
        <motion.span
          animate={reduced ? {} : { y: [0, 8, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown className="size-4" strokeWidth={1.5} />
        </motion.span>
      </div>

      <div className="absolute inset-x-0 bottom-0 z-10 translate-y-[22%] md:translate-y-[28%]">
        <div className="shell pb-[4.5rem] md:pb-0">
          <ul className="grid grid-cols-2 overflow-hidden rounded-sm border border-border/60 bg-card/95 shadow-soft backdrop-blur-md md:grid-cols-5">
            {amenityQuickBar.map((item, i) => {
              const Icon = iconMap[item.icon];
              return (
                <Reveal
                  key={item.id}
                  delay={i * 0.06}
                  className={i === amenityQuickBar.length - 1 ? "col-span-2 md:col-span-1" : ""}
                >
                  <li className="flex h-full flex-col items-center justify-center border-border/60 px-4 py-6 text-center md:border-r md:last:border-r-0 md:px-5 md:py-7 [&:nth-child(-n+4)]:border-b md:[&:nth-child(-n+4)]:border-b-0 [&:nth-child(odd)]:border-r md:[&:nth-child(odd)]:border-r-0">
                    <Icon className="size-5 text-primary" strokeWidth={1.4} aria-hidden="true" />
                    <p className="eyebrow mt-3 text-foreground">{item.title}</p>
                    <p className="mt-2 text-xs leading-snug text-muted-foreground">{item.label}</p>
                  </li>
                </Reveal>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}
